import React, { Component } from 'react';
import {
  View,
  Dimensions,
  ScrollView,
  TouchableWithoutFeedback,
  Keyboard,
  StatusBar,
  Platform,
} from 'react-native';
import LinearGradient from 'react-native-linear-gradient';
import Modal from 'react-native-modal';

import { ViewPropTypes } from 'deprecated-react-native-prop-types';
import _ from 'lodash';
import propTypes from 'prop-types';

import FoundationTestKeys from '../../FoundationTestKeys';
import { Icon } from '../Icon';
import { Images, Types, withTheme, LightThemeColors } from '../Themes';
import VFText from '../VFText';

import styles from './QuickActionMenu.Style';

const CloseIcon = Object.freeze({
  [Types.PRIMARY]: () => Images.ic_close_white,
  [Types.SECONDARY]: theme => (theme.isDark ? Images.ic_close_white : Images.ic_close),
});

const GradientbackgtoundColors = Object.freeze({
  [Types.PRIMARY]: theme => [...theme.colors.quickActionGradientColors[Types.PRIMARY]],
  [Types.SECONDARY]: theme => [...theme.colors.quickActionGradientColors[Types.SECONDARY]],
});

class QuickActionMenu extends Component {
  constructor(props) {
    super(props);

    this.isLargeContent = false;
    this.marginTop = 0;
    this.screenHeight =
      Dimensions.get('window').height + (Platform.OS === 'android' ? StatusBar.currentHeight : 0);
    this.scrollable = props.avoidKeyboard ?? false;

    this.state = {
      keyboardHeight: 0,
    };
  }

  componentDidMount() {
    if (Platform.OS === 'ios') {
      this.subscriptions = [Keyboard.addListener('keyboardWillChangeFrame', this.onKeyboardChange)];
    } else {
      this.subscriptions = [
        Keyboard.addListener('keyboardDidHide', this.onKeyboardChange),
        Keyboard.addListener('keyboardDidShow', this.onKeyboardChange),
      ];
    }
  }

  componentWillUnmount() {
    this.subscriptions.forEach(subscription => subscription.remove());
  }

  onKeyboardChange = keyboardEvent => {
    if (keyboardEvent == null) {
      this.setState({ keyboardHeight: 0 });
      return;
    }

    const height =
      this.screenHeight !== keyboardEvent.endCoordinates.screenY
        ? keyboardEvent.endCoordinates.height
        : 0;

    if (this.state.keyboardHeight === height) {
      return;
    }
    this.setState({ keyboardHeight: height });
  };

  onLayout = ({ nativeEvent }) => {
    // 100 was the default height adjustment values
    // when avoidKeyboard is true, Modal tends to shrink the height by some value, extra height of
    // 110 seemed to fix it
    const h = 100 + Number(!!this.props.avoidKeyboard && 110);
    const marginHeight = this.screenHeight - (nativeEvent.layout.height + h);

    if (marginHeight / this.screenHeight < 0.05) {
      this.marginTop = 0.05 * this.screenHeight;
      this.scrollable = this.isLargeContent = true;
    } else {
      this.marginTop = marginHeight;
    }
    this.forceUpdate();
  };

  onModalWillShow = () => _.isFunction(this.props.onModalWillShow) && this.props.onModalShow();
  onModalWillHide = () => {
    if (_.isFunction(this.props.onModalWillHide)) {
      setTimeout(() => this.props.onModalWillHide(), 90);
    }
  };

  toggleModal = () => _.isFunction(this.props.toggleModal) && this.props.toggleModal();
  closeModal = () => {
    const { toggleModal, closeModal } = this.props;
    if (_.isFunction(closeModal)) {
      closeModal();
    } else if (_.isFunction(toggleModal)) {
      toggleModal();
    }
  };

  calcModalMargin = () => {
    this.scrollable = this.isLargeContent;

    if (!this.props.withKeyboardAvoid || this.state.keyboardHeight === 0) {
      return this.marginTop;
    } else if (this.marginTop - this.state.keyboardHeight < 0) {
      this.scrollable = true;
      return Platform.OS === 'android' ? StatusBar.currentHeight : 36;
    }

    return this.marginTop - this.state.keyboardHeight;
  };

  render() {
    const { theme } = this.props;
    return (
      <Modal
        onBackButtonPress={this.toggleModal}
        animationIn="slideInUp"
        backdropTransitionInTiming={650}
        backdropTransitionOutTiming={0} // to fix bug in the library as the screen flashes when model is closed
        style={[styles.modalStyle, { marginTop: this.calcModalMargin() }]}
        accessibilityLabel={this.props.mainViewAccessibilityLabel}
        testID={this.props.mainViewAccessibilityLabel ?? FoundationTestKeys.QAMModal}
        onModalWillShow={this.onModalWillShow}
        onModalWillHide={this.onModalWillHide}
        statusBarTranslucent
        {...this.props}
      >
        <LinearGradient
          colors={
            this.props.customGradientBackgroundColors ||
            GradientbackgtoundColors[this.props.type](theme) ||
            GradientbackgtoundColors[Types.PRIMARY](theme)
          }
          start={{ x: 1, y: 0 }}
          end={{ x: 0, y: 1 }}
          style={styles.gradientStyle}
          testID={FoundationTestKeys.QAMModalLinearGradient}
        >
          <View
            style={[styles.containerStyle, this.props.containerStyle]}
            testID={this.props.testID ?? FoundationTestKeys.QAMModalContainerView}
          >
            {!this.props.hideHeader &&
              <View
                style={[styles.headerStyle, this.props.headerStyle]}
                testID={FoundationTestKeys.QAMModalContainerHeaderView}
              >
                <View
                  style={[styles.backButtonAndTitleContainer]}
                  testID={FoundationTestKeys.QAMModalContainerTitleView}
                >
                  {this.props.isBackButton && (
                    <TouchableWithoutFeedback
                      onPress={this.props.onBackButtonPress}
                      testID={FoundationTestKeys.QAMModalContainerTWF}
                    >
                      <Icon
                        type="image"
                        name={this.props.backButtonIcon}
                        style={[styles.backButtonIcon, this.props.backBtnStyle]}
                        testID={FoundationTestKeys.QAMModalContainerTWFIcon}
                      />
                    </TouchableWithoutFeedback>
                  )}

                  {this.props.withTitle && (
                    <VFText
                      i18nKey={this.props.title}
                      placeHolders={this.props.titlePlaceholders}
                      style={[styles.titleStyle(this.props.type, theme), this.props.titleStyle]}
                      testKey={
                        this.props.TitleAccessibilityLabel ??
                        FoundationTestKeys.QAMModalContainerTitle
                      }
                      allowFontScaling={this.props.allowFontScaling}
                    />
                  )}
                </View>

                <View
                  style={styles.centerHeaderView}
                  testID={FoundationTestKeys.QAMModalContainerCenterHeaderView}
                >
                  {this.props.renderCenterHeader()}
                </View>

                {this.props.withCloseButton && (
                  <TouchableWithoutFeedback onPress={this.closeModal} accessible={false}>
                    <Icon
                      type="image"
                      testID={this.props.quickActionMenuCloseIconTestID}
                      accessibilityLabel={
                        this.props.closeIconAccessibilityLabel ??
                        FoundationTestKeys.quickActionMenuCloseIcon
                      }
                      name={CloseIcon[this.props.type](theme) || CloseIcon[Types.PRIMARY](theme)}
                      size={24}
                      style={[styles.closeButton, this.props.closeBtnStyle]}
                    />
                  </TouchableWithoutFeedback>
                )}
              </View>}

            <ScrollView
              showsVerticalScrollIndicator={false}
              contentContainerStyle={[
                styles.scrollViewContentContainer,
                this.props.contentContainerStyle,
              ]}
              scrollEnabled={this.props.notScrollable ? false : this.scrollable}
              testID={FoundationTestKeys.QAScrollView}
              {...this.props.scrollViewProps}
            >
              <View onLayout={this.onLayout}>{this.props.children}</View>
            </ScrollView>
          </View>
        </LinearGradient>
      </Modal>
    );
  }
}

QuickActionMenu.defaultProps = {
  type: Types.PRIMARY,
  theme: LightThemeColors,
  withTitle: true,
  customGradientBackgroundColors: undefined,
  isBackButton: false,
  backButtonIcon: Images.ic_arrow_left,
  onBackButtonPress: () => null,
  quickActionMenuCloseIconTestID: FoundationTestKeys.quickActionMenuCloseIcon,
  withCloseButton: true,
  renderCenterHeader: () => null,
  scrollViewProps: {},
  withKeyboardAvoid: false,
};

QuickActionMenu.propTypes = {
  isVisible: propTypes.bool.isRequired,
  title: propTypes.string,
  titlePlaceholders: propTypes.arrayOf(propTypes.string),
  withTitle: propTypes.bool,
  theme: propTypes.object,
  toggleModal: propTypes.func.isRequired,
  contentContainerStyle: ViewPropTypes.style,
  children: propTypes.oneOfType([propTypes.arrayOf(propTypes.node), propTypes.node]).isRequired,
  type: propTypes.oneOf([Types.PRIMARY, Types.SECONDARY]),
  TitleAccessibilityLabel: propTypes.string,
  closeIconAccessibilityLabel: propTypes.string,
  mainViewAccessibilityLabel: propTypes.string,
  closeModal: propTypes.func,
  customGradientBackgroundColors: propTypes.arrayOf(propTypes.string),
  containerStyle: propTypes.object,
  headerStyle: propTypes.object,
  titleStyle: propTypes.object,
  closeBtnStyle: propTypes.object,
  allowFontScaling: propTypes.bool,
  isBackButton: propTypes.bool,
  backButtonIcon: propTypes.shape({ uri: propTypes.string }),
  onBackButtonPress: propTypes.func,
  quickActionMenuCloseIconTestID: propTypes.string,
  onModalWillShow: propTypes.func,
  onModalWillHide: propTypes.func,
  onModalHide: propTypes.func,
  onModalShow: propTypes.func,
  testID: propTypes.string,
  withCloseButton: propTypes.bool,
  renderCenterHeader: propTypes.func,
  scrollViewProps: propTypes.object,
  backBtnStyle: propTypes.object,
  notScrollable: propTypes.bool,
  avoidKeyboard: propTypes.bool,
  withKeyboardAvoid: propTypes.bool,
};

export default withTheme(QuickActionMenu);
